import React, { useState, useRef, useCallback, useEffect } from 'react';

interface DraggablePanelProps {
  children: React.ReactNode;
  initialPosition: { x: number; y: number };
  title?: string;
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
  dragHandle?: string; // CSS selector for drag handle
}

/**
 * Draggable Panel Component
 * Makes any panel draggable throughout the screen
 * Supports custom drag handles and maintains position within viewport bounds
 */
export const DraggablePanel: React.FC<DraggablePanelProps> = ({
  children,
  initialPosition,
  title,
  onClose,
  className = '',
  style = {},
  dragHandle = '.drag-handle'
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle mouse down on drag handle
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Check if the target or its parent matches the drag handle selector
    const target = e.target as HTMLElement;
    const dragHandleElement = target.closest(dragHandle) || 
                             (target.matches && target.matches(dragHandle) ? target : null);
    
    if (!dragHandleElement) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;

    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  }, [dragHandle]);

  // Handle mouse move during drag
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Get panel dimensions
    const panel = panelRef.current;
    if (!panel) return;

    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Constrain to viewport bounds
    const constrainedX = Math.max(0, Math.min(newX, viewportWidth - panelRect.width));
    const constrainedY = Math.max(0, Math.min(newY, viewportHeight - panelRect.height));

    setPosition({ x: constrainedX, y: constrainedY });
  }, [isDragging, dragOffset]);

  // Handle mouse up to stop dragging
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners during drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle escape key to close panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      className={`fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 border-2 border-gray-200 dark:border-gray-600 ${className}`}
      style={{
        left: position.x + 'px',
        top: position.y + 'px',
        ...style
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Default drag handle if title is provided */}
      {title && (
        <div className="drag-handle flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-t-lg cursor-grab active:cursor-grabbing">
          <h3 className="text-lg font-semibold dark:text-white select-none">
            {title}
            {/* <span className="text-xs text-blue-500 bg-blue-100 px-2 py-1 rounded ml-2">[]</span> */}
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl font-bold p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              ✕
            </button>
          )}
        </div>
      )}
      
      {/* Panel content */}
      <div className={title ? '' : 'cursor-default'}>
        {children}
      </div>
    </div>
  );
};

export default DraggablePanel;
